import { BaseComponent } from '../../base-component';
import { ColorUiType, DimensionValueByType, DimensionsStateEvent, UNKNOWN_DIMENSION_TYPE_NAME } from '../shared-types';
import { decodeHtml } from '../../_common/client/decode-html';
import { DIMENSION_SELECTED_TOPIC } from '../../constants';
import { setupDimensionTiles } from './setup-dimension-tiles';
import { trackAvailableDimensionUiTypes as trackAvailableDimensionTypes, trackInvalidDimensions } from './tracking/tracking-helpers';
import { getCurrentDimensionsForDimensionStateEvent } from './get-current-dimensions-for-dimension-state-event';
import { updateDimensionSelectionPriorityInStorage } from './update-dimension-selection-priority-in-storage';
import { isEnabledDimensionInput } from './is-enabled-dimension-input';

export class DimensionSelectionComponent extends BaseComponent {
    public rootSelector = '.js_pdp_dimension-selection';
    private loadedDimensionsState: DimensionValueByType[] = [];

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }

        trackAvailableDimensionTypes(this.getRootElement());

        this.loadedDimensionsState = this.getDimensionsStateFromDataAttributes();

        trackInvalidDimensions(this.loadedDimensionsState);

        for (const dimensionBlock of this.getDimensionBlocks()) {
            let dimensionTilesAndDropDown = this.getDimensionTilesAndDropDown(dimensionBlock);
            dimensionTilesAndDropDown = this.resetAllDimensionTilesAndDropDown(dimensionTilesAndDropDown, dimensionBlock);

            const dimType = dimensionBlock.dataset.dimType || UNKNOWN_DIMENSION_TYPE_NAME;
            for (const dimensionButtonOrDropDown of dimensionTilesAndDropDown) {
                dimensionButtonOrDropDown.addEventListener('change', (event: Event) => {
                    const dimensionTypeUsed = (event.currentTarget as HTMLElement).closest<HTMLElement>('[data-dimension-type]')?.dataset
                        .dimensionType as ColorUiType | undefined;

                    this.dimensionChangedEventListener(dimType, dimensionButtonOrDropDown, dimensionTypeUsed);
                });
            }
        }

        this.initTeamAssetsDropdowns();

        setupDimensionTiles(this.getRootElement());
    }
    private resetAllDimensionTilesAndDropDown(
        dimensionButtonsAndDropDowns: (HTMLInputElement | HTMLSelectElement)[],
        dimensionBlock: HTMLElement
    ) {
        dimensionButtonsAndDropDowns.forEach(dimensionButtonOrDropDown => {
            dimensionButtonOrDropDown.replaceWith(dimensionButtonOrDropDown.cloneNode(true));
        });
        dimensionButtonsAndDropDowns = this.getDimensionTilesAndDropDown(dimensionBlock);
        return dimensionButtonsAndDropDowns;
    }

    private getDimensionTilesAndDropDown(dimensionBlock: HTMLElement) {
        const dimensionButtons = Array.from(dimensionBlock.querySelectorAll<HTMLInputElement>('input[type="radio"]'));
        const dimensionDropDowns = Array.from(
            dimensionBlock.querySelectorAll<HTMLSelectElement>('select.js_pdp_dimension-selection_dropdown')
        );

        return [...dimensionButtons, ...dimensionDropDowns];
    }

    private initTeamAssetsDropdowns(): void {
        o_global.eventQBus.emit('assets.dropdown.init', this.rootSelector);
    }

    /**
     * reads from HTML, rendered from server, dimensions state as object
     */
    private getDimensionsStateFromDataAttributes(): DimensionValueByType[] {
        const selectedDimensionsEncoded = this.getRootElement().dataset.selectedStateEncoded;
        if (!selectedDimensionsEncoded) {
            throw new Error('cant parse encoded selected dimensions');
        }
        return JSON.parse(decodeHtml(selectedDimensionsEncoded));
    }

    private dimensionChangedEventListener(
        dimType: string,
        control: HTMLInputElement | HTMLSelectElement,
        dimensionTypeUsed: ColorUiType | undefined
    ): void {
        const selectedDimensionsState = this.getSelectedDimensionState(dimType, control);

        const productId = this.getRootElement().getAttribute('data-product-id');
        if (productId) {
            selectedDimensionsState.forEach(selectedDimension =>
                updateDimensionSelectionPriorityInStorage(productId, selectedDimension.type)
            );
        }

        const dimensionsStateEvent: DimensionsStateEvent = {
            dimensionStates: getCurrentDimensionsForDimensionStateEvent(this.loadedDimensionsState, selectedDimensionsState, productId),
            selectedState: selectedDimensionsState,
            dimensionTypeUsed,
            dimensionEnabled: isEnabledDimensionInput(control),
        };

        o_global.eventQBus.emit(DIMENSION_SELECTED_TOPIC, dimensionsStateEvent);
    }

    /**
     *
     * @param dimType dimension type name
     * @param control changed control of any dimension block
     * @returns selected dimension filters object
     */
    private getSelectedDimensionState(dimType: string, control: HTMLInputElement | HTMLSelectElement): DimensionValueByType[] {
        return [{ type: dimType, value: control.value }];
    }

    /**
     * @returns array of html blocks of dimensions. One html block per dimension
     */
    private getDimensionBlocks(): Array<HTMLElement> {
        return this.getChildElements('[data-dim-type]');
    }
}
